import React, { useMemo, useState, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
// import { scaleQuantize } from 'd3-scale';
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import axios from "axios";
import { AUTH_URL } from "../../utils/constants";
// import { truncateText } from '../../utils/utils';
import TruncatedText from "../../utils/TruncatedText";
import { toCamelCase, toSentenceCase } from "../../utils/utils";
import { useAuth } from "../../utils/AuthContext";
import PieCharts from "./PieCharts";
import upDownArrow from "../../assets/images/up-down-arrow.svg";
import { getGrievance } from "../adminDashboard/AdminUser";
import Swal from "sweetalert2";
const GRADIENT_COLORS = ["#0041C0", "#5f8dea"];

export default function DashboardStatesUser({ dashboardData, data }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [showCommentArea, setShowCommentArea] = useState(false);
  const [isLiked, setIsLiked] = useState(true);
  const [comment, setComment] = useState(""); // New state to store comment
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [queryUpdate, setQueryUpdate] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [pieChartData, setPieChartData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchParam, setSearchParam] = useState("All");

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const { user } = useAuth();

  const normalizeStatus = (status) => {
    const s = (status || "pending").toLowerCase();
    if (s === "pending") return "Pending";
    if (s === "resolved" || s === "closed") return "Resolved";
    if (s === "rejected") return "Rejected";
    if (s === "responded") return "Responded";
    return "Other";
  };

  // Derive counts and groupings
  const stats = useMemo(() => {
    // Use API pie chart data if available
    let statusPieData;

    if (pieChartData) {
      statusPieData = [
        {
          name: "Pending",
          value: pieChartData.pending.count,
          // percentage: pieChartData.pending.percentage,
        },
        {
          name: "In Progress",
          value: pieChartData.inProgress.count,
          // percentage: pieChartData.responded.percentage,
        },
        {
          name: "Resolved",
          value: pieChartData.resolved.count,
          // percentage: pieChartData.responded.percentage,
        },
      ];
    } else {
      // Fallback: Handle old backend format with summary
      const statusSummary =
        Array.isArray(dashboardData.summary) && dashboardData.summary.length
          ? (() => {
              const aggregated = {};
              dashboardData.summary.forEach((item) => {
                const normalizedStatus = normalizeStatus(item.status);
                if (!aggregated[normalizedStatus]) {
                  aggregated[normalizedStatus] = {
                    name: normalizedStatus,
                    value: 0,
                    percentage: 0,
                  };
                }
                aggregated[normalizedStatus].percentage += item.percentage || 0;
              });
              return Object.values(aggregated).map((item) => ({
                ...item,
                value: item.percentage,
              }));
            })()
          : null;

      const statusCounts = {};
      statusCounts["Pending"] = 0;
      statusCounts["Responded"] = 0;

      data.forEach((item) => {
        const normalizedStatus = normalizeStatus(item.status);
        if (statusCounts[normalizedStatus] !== undefined) {
          statusCounts[normalizedStatus]++;
        }
      });

      statusPieData = statusSummary ?? [
        { name: "Pending", value: statusCounts["Pending"] },
        { name: "Responded", value: statusCounts["Responded"] },
      ];
    }

    const categories = {};
    const normalizedData = data.map((item) => {
      const normalizedStatus = normalizeStatus(item.status);

      const category = item.llm_subcategory || "Other";
      categories[category] = (categories[category] || 0) + 1;

      return {
        ...item,
        normalized_status: normalizedStatus,
      };
    });

    return {
      total: pieChartData?.total ?? dashboardData.total ?? data.length,
      statusPieData,
      categories: Object.entries(categories).map(([name, value]) => ({
        name,
        value,
      })),
      allGrievances: normalizedData,
    };
  }, [data, dashboardData, pieChartData]);

  const filteredGrievances = useMemo(() => {
    let result = stats.allGrievances;
    if (statusFilter !== "All") {
      result = result.filter(
        (item) => item.user_grievance_status === statusFilter,
      );
    }
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter((item) => {
        // Filter only by grievance ID as per request
        return (
          item.grievance_id &&
          item.grievance_id.toLowerCase().includes(lowerQuery)
        );
      });
    }

    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case "grievance_id":
            aValue = a.grievance_id || "";
            bValue = b.grievance_id || "";
            break;
          case "reg_date":
            aValue = new Date(a.grievance_submission_timestamp || 0).getTime();
            bValue = new Date(b.grievance_submission_timestamp || 0).getTime();
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [stats.allGrievances, statusFilter, searchQuery, searchParam, sortConfig]);

  const handleViewClick = async (item) => {
    const res = await getGrievance(item.grievance_id);
    if (res) {
      setSelectedRow(res);
      setIsViewOnly(true); // Set view-only mode
      setIsModalOpen(true);
    }
    // setSelectedRow(item);
    // setIsViewOnly(true); // Set view-only mode
    // setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedRow(null);
  };

  // Fetch pie chart percentages from the new API
  useEffect(() => {
    const fetchPieChartData = async () => {
      try {
        if (data && data.length > 0) {
          const userId = data[0].user_id;
          if (userId) {
            const res = await axios.get(
              `${AUTH_URL}api/pendingrespondedpercentageuser?user_id=${userId}`,
            );
            if (res.data && res.data.success) {
              setPieChartData({
                pending: {
                  count: dashboardData.pending,
                  // percentage: res.data.pending_percentage,
                },
                inProgress: {
                  count: dashboardData.wip,
                  // percentage: res.data.responded_percentage,
                },
                resolved: {
                  count: dashboardData.resolved,
                  // percentage: res.data.responded_percentage,
                },
                total: res.data.total_grievances,
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching pie chart data:", error);
        // Fallback to local calculation if API fails
      }
    };

    fetchPieChartData();
  }, [data]);

  const handleLikeClick = (e) => {
    e.preventDefault();
    setIsLiked(true);
    setShowCommentArea(true); // Hide comment area after liking
  };

  const handleDislikeClick = (e) => {
    e.preventDefault();
    setIsLiked(false);
    setShowCommentArea(true); // Show comment area after disliking
  };

  const handleSave1 = async () => {
    setSaveLoading(true);
    try {
      const feedbackData = {
        user_id: user.userId,
        grievance_id: selectedRow.grievance_id, // Assuming you have selectedRow.id for grievance ID
        like_dislike: isLiked, // 1 for like, 0 for dislike
        comments: comment, // The comment entered by the user
      };

      const res = await axios.post(
        `${AUTH_URL}/api/user_grievance_feedback`,
        feedbackData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (res.status === 200) {
        console.log("Feedback saved successfully");
        Swal.fire({
          title: "Feedback Saved Successfully!",
          icon: "success",
        });
        // Optionally, reset the states after saving
        setIsLiked(false);
        setComment("");
        setShowCommentArea(false);
      }
    } catch (error) {
      console.error("Error saving feedback:", error);
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-100  font-sans">
      {/* Summary Boxes */}
      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-indigo-600 text-white rounded-2xl p-6 flex justify-between items-center shadow-lg hover:scale-[1.02] transition-transform duration-200 ease-in-out">
          <div>
            <p className="text-sm font-medium text-indigo-100 mb-1">Total User Queries Registered</p>
            <p className="text-4xl font-extrabold tracking-tight">{stats.total}</p>
          </div>
          <div className="bg-indigo-500 p-3 rounded-full">
            <HelpOutlineIcon fontSize="large" className="text-white" />
          </div>
        </div>

        <div className="bg-amber-500 text-white rounded-2xl p-6 flex justify-between items-center shadow-lg hover:scale-[1.02] transition-transform duration-200 ease-in-out">
          <div>
            <p className="text-sm font-medium text-yellow-100 mb-1">Number of User Queries Open</p>
            <p className="text-4xl font-extrabold tracking-tight">{dashboardData.pending || 0}</p>
          </div>
          <div className="bg-amber-400 p-3 rounded-full">
            <HourglassTopIcon fontSize="large" className="text-white" />
          </div>
        </div>
        <div className="bg-amber-500 text-white rounded-2xl p-6 flex justify-between items-center shadow-lg hover:scale-[1.02] transition-transform duration-200 ease-in-out">
          <div>
            <p className="text-sm font-medium text-yellow-100 mb-1">Number of User Queries In Progress</p>
            <p className="text-4xl font-extrabold tracking-tight">{dashboardData.wip || 0}</p>
          </div>
          <div className="bg-amber-400 p-3 rounded-full">
            <HourglassTopIcon fontSize="large" className="text-white" />
          </div>
        </div>

        <div className="bg-emerald-600 text-white rounded-2xl p-6 flex justify-between items-center shadow-lg hover:scale-[1.02] transition-transform duration-200 ease-in-out">
          <div>
            <p className="text-sm font-medium text-emerald-100 mb-1">Number of User Queries Resolved</p>
            <p className="text-4xl font-extrabold tracking-tight">{dashboardData.resolved || 0}</p>
          </div>
          <div className="bg-emerald-500 p-3 rounded-full">
            <CheckCircleOutlineIcon fontSize="large" className="text-white" />
          </div>
        </div>
      </div> */}

      {/* Charts Section */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
       

        <div className='bg-white shadow rounded-lg p-4 w-full h-[400px]'>
          <PieCharts
            data={stats.statusPieData}
            nameKey="name"
            title="Grievance Status"
            xAxisName="Number of Queries"
            colorMap={{
              "Resolved": "#10B981",
              "Attach ATR": "#F59E0B",
              "Add Comment": "#6366F1"
            }}
            colors={["#6366F1", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6"]}
          />
        </div>

        <div className="bg-white shadow rounded-lg p-6 w-full">
          <p className="font-semibold mb-2">Grievances by Category</p>
          {stats.categories.map((cat, idx) => {
            const percent = ((cat.value / stats.total) * 100).toFixed(0);
            return (
              <div key={idx} className="mb-3">
                <div className="flex justify-between text-sm">
                  <span>{cat.name}</span>
                  <span>{percent}%</span>
                </div>
                <div className="bg-gray-200 h-2 rounded mt-1">
                  <div className="h-2 bg-gradient-to-r from-[#FF852B] via-[#D37929] to-[#B34700] rounded" style={{ width: `${percent}%` }}></div>
                </div>
              </div>
            );
          })}
        </div>
      </div> */}

      {/* Grievance Table */}
      <div className="bg-white p-4 w-full">
        {/* <p className="font-semibold mb-2">Grievances Over Time</p>
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-gray-600 border-b">
              <th className="py-2">S. No.</th>
              <th className="py-2">Grievance ID</th>
              <th className="py-2">User Query</th>
              <th className="py-2">Category</th>
              <th className="py-2">Status</th>
              <th className="py-2">Date</th>
              <th className="py-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {stats.allGrievances.map((row, idx) => (
              <tr key={idx} className="border-b hover:bg-gray-100 transition">
                {
                  console.log('row', row)
                }
                <td className="py-2">{idx + 1}</td>
                <td className="py-2">{row.grievance_id}</td>
                <td className="py-2">{row.user_query}</td>
                <td className="py-2">{`${row.llm_category}(${row.llm_subcategory})`}</td>
                <td className="py-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold
                    ${row.user_grievance_status?.toLowerCase() === 'pending' ? 'bg-amber-100 text-amber-700' :
                      row.user_grievance_status?.toLowerCase() === 'resolved' || "responded" ? 'bg-emerald-100 text-emerald-700' :
                        'bg-red-100 text-red-700'}
                  `}>
                    {row.status}
                  </span>
                </td>
                <td className="py-2">{row.grievance_submission_timestamp}</td>
                <td className="py-2">
                  <div className="flex justify-center items-center gap-2">
                    {row.user_grievance_status?.toLowerCase() === "pending" ? (
                      <VisibilityIcon fontSize="small" className="text-[#FF852B] cursor-pointer" onClick={() => handleViewClick(row)} />
                    ) : (
                      <EditIcon fontSize="small" className="text-[#B34700] cursor-pointer" />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table> */}
        <div className="bg-white p-6 w-full">
          <p className="text-lg font-semibold text-gray-800 mb-4 ">
            Grievances Over Time
          </p>
          <div className="flex items-center gap-4 mb-4">
            {/*
            <select
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring focus:ring-indigo-200 bg-white"
            >
              <option value="All">All Parameters</option>
              <option value="Grievance ID">Grievance ID</option>
              <option value="User Query">User Query</option>
              <option value="Department">Department</option>
              <option value="Status">Status</option>
              <option value="Reg. Date">Reg. Date</option>
            </select>
            */}
            <input
              type="text"
              placeholder={`Search by Grievance ID...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring focus:ring-indigo-200 w-full sm:w-72"
            />
          </div>
          <div className="border border-gray-200 rounded-lg">
            <div className="">
              <div className="overflow-x-auto">
                <table className="min-w-[800px] sm:min-w-full text-sm text-center">
                  <thead className="bg-gray-100 text-gray-700 sticky top-0 ">
                    <tr>
                      <th className="py-3 px-1 border-b">S. No.</th>
                      <th
                        className="py-3 px-1 border-b cursor-pointer hover:bg-gray-200"
                        onClick={() => requestSort("grievance_id")}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Grievance ID{" "}
                          <img
                            src={upDownArrow}
                            alt="sort"
                            className="w-3 h-3 inline"
                          />
                        </div>
                      </th>
                      <th className="py-3 px-1 border-b">User Grievance</th>
                      <th className="py-3 px-1 border-b">Department</th>
                      <th className="py-3 px-1 border-b">
                        <div className="flex flex-col gap-2">
                          <span>Status</span>
                          <select
                            id="statusFilter"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring focus:ring-indigo-200"
                          >
                            <option value="All">All</option>
                            <option value="Open">Open</option>
                            <option value="InProgress">In Progress</option>
                            <option value="Resolved">Resolved</option>
                          </select>
                        </div>
                      </th>
                      <th
                        className="py-3 px-1 border-b cursor-pointer hover:bg-gray-200"
                        onClick={() => requestSort("reg_date")}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Date{" "}
                          <img
                            src={upDownArrow}
                            alt="sort"
                            className="w-3 h-3 inline"
                          />
                        </div>
                      </th>
                      <th className="py-3 px-1 border-b text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGrievances.map((row, idx) => (
                      <tr
                        key={idx}
                        className="border-b hover:bg-gray-50 transition"
                      >
                        <td className="py-2 px-1">{idx + 1}</td>
                        <td className="py-2 px-1 font-mono text-indigo-600">
                          <TruncatedText text={row.grievance_id} limit={20} />
                        </td>
                        <td className="py-2 px-1 text-gray-800">
                          <TruncatedText text={row.user_query} limit={25} />
                        </td>
                        <td className="py-2 px-1">
                          <TruncatedText
                            text={`${row.llm_category} (${row.llm_subcategory})`}
                            limit={25}
                          />
                        </td>
                        <td className="py-2 px-1">
                          <span
                            className={`min-w-[100px] inline-block text-center px-3 py-1 text-xs font-medium rounded-full
                                ${
                                  row.user_grievance_status?.toLowerCase() ===
                                  "resolved"
                                    ? "bg-green-600 text-white" // dark yellow
                                    : "bg-yellow-600 text-white" // green
                                }
    `}
                          >
                            {toSentenceCase(row.user_grievance_status || "")}
                          </span>
                        </td>
                        <td className="py-2 px-1 text-gray-600">
                          {new Date(
                            row.grievance_submission_timestamp,
                          ).toLocaleDateString("en-IN")}
                        </td>
                        <td className="py-2 px-1 text-center">
                          <div className="flex justify-center items-center gap-2">
                            <VisibilityIcon
                              fontSize="small"
                              className={`
    cursor-pointer
    ${
      row.user_grievance_status?.toLowerCase() === "closed"
        ? "text-green-600 hover:text-green-700"
        : "text-yellow-600 hover:text-yellow-700"
    }
  `}
                              onClick={() => handleViewClick(row)}
                            />
                            {/* {row.user_grievance_status?.toLowerCase() !== 'pending' ? (
                          <VisibilityIcon
                            fontSize="small"
                            className="text-orange-500 hover:text-orange-600 cursor-pointer"
                            onClick={() => handleViewClick(row)}
                          />
                        ) : (
                          <EditIcon
                            fontSize="small"
                            className="text-red-600 hover:text-red-700 cursor-pointer"
                            onClick={() => handleViewClick(row)}
                          />
                        )} */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Modal */}
          {isModalOpen && selectedRow && (
            <div className="fixed inset-0 flex items-start sm:items-center justify-center bg-black bg-opacity-50 p-4 sm:p-0">
              <div className="bg-white w-full sm:w-2/3 max-w-2xl p-5 sm:p-6 rounded shadow-lg max-h-[85vh] sm:max-h-[90vh] overflow-auto mt-6 sm:mt-0">
                <h3 className="text-lg font-bold mb-4">View Query</h3>
                <form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      User Query
                    </label>
                    <input
                      type="text"
                      name="user_query"
                      value={selectedRow.user_query}
                      readOnly={isViewOnly}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Bot Response
                    </label>
                    <textarea
                      type="text"
                      name="llm_response_desc"
                      value={selectedRow.llm_response_desc}
                      readOnly={isViewOnly}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                      rows={3} // You can adjust the number of rows as per your requirement
                    ></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Comment from officer
                    </label>
                    <textarea
                      name="officer_grievance_comments"
                      value={
                        selectedRow?.officer_grievance_comments ||
                        selectedRow?.grievance_response
                      }
                      readOnly={isViewOnly}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                      rows={3} // You can adjust the number of rows as per your requirement
                    ></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      User Id
                    </label>
                    <input
                      type="text"
                      name="user_id"
                      value={selectedRow.user_id}
                      readOnly={isViewOnly}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                    />
                  </div>
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Ministry
                    </label>
                    <input
                      type="text"
                      name="llm_ministry"
                      value={selectedRow.llm_ministry}
                      readOnly={isViewOnly}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                    />
                  </div> */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Department
                    </label>
                    <input
                      type="text"
                      name="llm_category"
                      value={selectedRow.llm_category}
                      readOnly={isViewOnly}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Sub Category
                    </label>
                    <input
                      type="text"
                      name="llm_subcategory"
                      value={selectedRow.llm_subcategory}
                      readOnly={isViewOnly}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      State
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={selectedRow.state || ""}
                      readOnly={isViewOnly}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      District
                    </label>
                    <input
                      type="text"
                      name="district"
                      value={selectedRow.district}
                      readOnly={isViewOnly}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                    />
                  </div>
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={selectedRow.city}
                      readOnly={isViewOnly}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                    />
                  </div> */}
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Severity
                    </label>
                    <input
                      type="text"
                      name="severity"
                      value={selectedRow.severity}
                      readOnly={isViewOnly}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                    />
                  </div> */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <input
                      type="text"
                      name="status"
                      value={selectedRow.user_grievance_status}
                      readOnly={isViewOnly}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                    />
                  </div>
                  {selectedRow.user_grievance_status === "Resolved" && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          FeedBack
                        </label>
                      </div>
                      {selectedRow.user_grievance_feedback_comments ? (
                        <input
                          type="text"
                          name="status"
                          value={selectedRow.user_grievance_feedback_comments}
                          readOnly={isViewOnly}
                          className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                        />
                      ) : (
                        <div>
                          {/* {selectedRow.user_grievance_status === "Resolved" && ( */}
                          <button
                            className={` px-4 py-2 text-white rounded mr-2 ${
                              isLiked
                                ? "bg-blue-700 hover:bg-blue-800"
                                : "bg-blue-500 hover:bg-blue-600"
                            }`}
                            onClick={handleLikeClick}
                          >
                            Satisfied
                          </button>
                          {/* )} */}

                          {/* {selectedRow.user_grievance_status === "Resolved" && ( */}
                          <button
                            className={`px-4 py-2  text-white rounded ${
                              !isLiked
                                ? "bg-red-700 hover:bg-red-800"
                                : "bg-red-500 hover:bg-red-600"
                            }`}
                            onClick={handleDislikeClick}
                          >
                            Not Satisfied
                          </button>
                          {/* )} */}

                          {showCommentArea && (
                            <div className="mt-4">
                              <textarea
                                placeholder="Please provide your comment..."
                                className="w-full p-2 border border-gray-300 rounded"
                                rows="4"
                                onChange={(e) => setComment(e.target.value)} // Save comment to state
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                      onClick={handleClose}
                    >
                      Close
                    </button>
                    {showCommentArea &&
                      selectedRow.user_grievance_status?.toLowerCase() ===
                        "resolved" && (
                        <button
                          type="button"
                          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                          onClick={handleSave1}
                        >
                          {saveLoading ? "Saving..." : "Save"}
                        </button>
                      )}
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
